import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import BuildIcon from "@mui/icons-material/Build";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const copper = "#B87333";
const copperDark = "#935C29";

function previewEntityName(preview) {
  const entity = preview?.selected_entity;
  return entity?.display_name || entity?.name || preview?.title || "Casper route";
}

function previewModeLabel(preview) {
  const mode = String(preview?.mode || "").replace(/_/g, " ");
  if (!mode) return "Route";
  return mode.charAt(0).toUpperCase() + mode.slice(1);
}

function previewPrimaryIcon(preview) {
  const mode = preview?.mode;
  const entityType = preview?.selected_entity?.type;
  if (mode === "workflow" || entityType === "workflow") return <AccountTreeIcon fontSize="small" />;
  if (mode === "tool" || entityType === "tool") return <BuildIcon fontSize="small" />;
  if (mode === "knowledge" || entityType === "knowledge") return <MenuBookIcon fontSize="small" />;
  if (mode === "blocked") return <WarningAmberIcon fontSize="small" />;
  return <PsychologyIcon fontSize="small" />;
}

export default function CasperExecutionPreviewCard({
  preview,
  loading = false,
  onRun,
  onChange,
  onDirectAnswer,
  onOpenSetup,
  onCopyAdminNote,
}) {
  const theme = useTheme();
  if (!preview) return null;
  const isDark = theme.palette.mode === "dark";
  const mode = preview.mode;
  const decision = preview.decision;
  const blocked = decision === "blocked" || mode === "blocked";
  const runnable = ["auto_run_allowed", "confirmation_required"].includes(decision);
  const showMatchConfidence = !blocked && Number.isFinite(Number(preview.confidence)) && Number(preview.confidence) > 0;
  const steps = Array.isArray(preview.workflow_plan?.steps) ? preview.workflow_plan.steps : [];
  const alternatives = Array.isArray(preview.alternatives) ? preview.alternatives : [];
  const riskReasons = Array.isArray(preview.risk_reasons) ? preview.risk_reasons : [];
  const surface = isDark ? alpha("#241B15", 0.96) : "rgba(255,252,249,0.96)";
  const softSurface = isDark ? alpha("#B87333", 0.12) : "rgba(184,115,51,0.08)";
  const border = isDark ? "rgba(224,177,132,0.32)" : "rgba(184,115,51,0.24)";
  const title = preview.title || `I found ${previewEntityName(preview)}`;
  const summary = preview.summary || "Casper found the best route for this request.";
  const reason = preview.reason || "This is the best match based on your prompt and available capabilities.";
  const actions = Array.isArray(preview.actions) ? preview.actions : [];
  const directAnswerAction = actions.find((action) => action?.action === "direct_answer");

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1.2,
        p: { xs: 1.35, md: 1.55 },
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: border,
        bgcolor: surface,
      }}
    >
      <Stack spacing={1.1}>
        <Stack direction="row" spacing={1.1} alignItems="flex-start">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: blocked ? alpha("#F97316", 0.12) : softSurface,
              color: blocked ? "#C2410C" : copperDark,
              flex: "0 0 auto",
            }}
          >
            {previewPrimaryIcon(preview)}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" fontWeight={950}>
                {title}
              </Typography>
              <Chip
                size="small"
                label={previewModeLabel(preview)}
                sx={{
                  height: 22,
                  fontWeight: 850,
                  bgcolor: softSurface,
                  color: isDark ? "#F2C69B" : copperDark,
                }}
              />
              {showMatchConfidence && (
                <Chip
                  size="small"
                  label={`${Math.round(Number(preview.confidence) * 100)}% match`}
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontWeight: 800,
                    borderColor: border,
                    color: "text.secondary",
                  }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
              {summary}
            </Typography>
          </Box>
        </Stack>

        <Box
          sx={{
            p: 1.1,
            borderRadius: 2,
            bgcolor: isDark ? alpha("#FFFFFF", 0.045) : "rgba(246,240,235,0.54)",
            border: "1px solid",
            borderColor: isDark ? alpha("#FFFFFF", 0.07) : "rgba(184,115,51,0.12)",
          }}
        >
          <Typography variant="caption" fontWeight={900} color={isDark ? "#F2C69B" : copperDark}>
            Why this route
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
            {reason}
          </Typography>
        </Box>

        {steps.length > 0 && (
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: isDark ? alpha("#FFFFFF", 0.07) : "rgba(184,115,51,0.12)",
              bgcolor: "transparent",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight={900}>
                Workflow plan · {steps.length} step{steps.length === 1 ? "" : "s"}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={0.8}>
                {steps.map((step, index) => (
                  <Stack
                    key={step.id || `${step.user_label || step.agent_name || "step"}-${index}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      p: 0.85,
                      borderRadius: 1.5,
                      bgcolor: isDark ? alpha("#FFFFFF", 0.04) : "rgba(255,255,255,0.68)",
                    }}
                  >
                    <Chip
                      size="small"
                      label={index + 1}
                      sx={{
                        minWidth: 28,
                        fontWeight: 900,
                        bgcolor: softSurface,
                        color: isDark ? "#F2C69B" : copperDark,
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={850}>
                        {step.user_label || step.agent_name || step.id || `Step ${index + 1}`}
                      </Typography>
                      {(step.agent_name || step.capability || step.type) && (
                        <Typography variant="caption" color="text.secondary">
                          {[step.agent_name, step.capability, step.type].filter(Boolean).join(" · ")}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {(riskReasons.length > 0 || alternatives.length > 0) && (
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              borderRadius: 2,
              bgcolor: "transparent",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="caption" fontWeight={900} color="text.secondary">
                Details
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {riskReasons.length > 0 && (
                  <Box>
                    <Typography variant="caption" fontWeight={900} color="text.secondary">
                      Checks before running
                    </Typography>
                    <Stack spacing={0.45} sx={{ mt: 0.55 }}>
                      {riskReasons.map((item) => (
                        <Typography key={item} variant="caption" color="text.secondary">
                          {item}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
                {alternatives.length > 0 && (
                  <Box>
                    <Typography variant="caption" fontWeight={900} color="text.secondary">
                      Other possible matches
                    </Typography>
                    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mt: 0.55 }}>
                      {alternatives.slice(0, 4).map((entity) => (
                        <Chip
                          key={`${entity.type}-${entity.id}-${entity.version || ""}`}
                          size="small"
                          label={entity.display_name || entity.name || entity.type}
                          variant="outlined"
                          sx={{ fontWeight: 750, borderColor: border }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        <Stack direction="row" spacing={0.8} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
          {blocked && preview.setup_action_allowed && (
            <Button
              size="small"
              variant="contained"
              startIcon={<VpnKeyIcon />}
              onClick={onOpenSetup}
              disabled={loading}
              sx={{ bgcolor: copper, "&:hover": { bgcolor: copperDark }, fontWeight: 900 }}
            >
              Open setup
            </Button>
          )}
          {blocked && !preview.setup_action_allowed && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => onCopyAdminNote?.(preview.admin_note || summary)}
              sx={{ borderColor: border, color: isDark ? "#F2C69B" : copperDark, fontWeight: 900 }}
            >
              Copy note for admin
            </Button>
          )}
          {!blocked && preview.can_change_selection && (
            <Button
              size="small"
              variant="outlined"
              onClick={onChange}
              disabled={loading}
              sx={{ borderColor: border, color: isDark ? "#F2C69B" : copperDark, fontWeight: 900 }}
            >
              Change
            </Button>
          )}
          {!blocked && directAnswerAction && (
            <Button
              size="small"
              variant="contained"
              onClick={onDirectAnswer}
              disabled={loading}
              sx={{ bgcolor: copper, "&:hover": { bgcolor: copperDark }, fontWeight: 950 }}
            >
              {directAnswerAction.label || "Answer directly"}
            </Button>
          )}
          {runnable && (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={onRun}
              disabled={loading}
              sx={{ bgcolor: copper, "&:hover": { bgcolor: copperDark }, fontWeight: 950 }}
            >
              Run
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
